import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  CardHeader,
  Avatar,
  IconButton,
  Typography,
  Tooltip,
  CardContent,
  Button,
  CardActions,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PlayArrow, AddCircle, RemoveCircle } from '@mui/icons-material';

import DocumentTable from './DocumentTable';
import DocumentDetail from './DocumentDetail';
import DocumentUpload from './DocumentUpload';
import LoadingSpinner from './LoadingSpinner';
import {
  postDocument,
  deleteDocument,
  queryDocument,
} from '../services/apiService';

import { showToast } from '../reducers/uiReducer';
import { setLoading } from '../reducers/loadingReducer';
import { addMessage } from '../reducers/messagesReducer';
import {
  setShowTiles,
  setIsActiveChat,
  setSelectedTileId,
  addPromptItem,
  selectedPromptItem,
  removePromptItem,
  setSelectedPromptItem,
  clearSelectedPromptItem,
} from '../reducers/uiReducer';
import {
  setSelectedDocument,
  clearSelectedDocument,
} from '../reducers/documentsReducer';
import SelectedTileHeader from './SelectedTileHeader';

const DocumentManager = ({ show, onClose, icon, title, setPromptCallBack }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false); // State for editing
  const loading = useSelector((state) => state.loading);
  const documents = useSelector((state) => state.documents);
  const selectedItem = useSelector(selectedPromptItem);
  const selectedDocument = useSelector(
    (state) => state.documents.selectedDocument,
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showSelectedDocument, setShowSelectedDocument] = useState(false);

  const handleClose = () => {
    setShowDocumentUpload(false);
    dispatch(setSelectedDocument(null));
    dispatch(setSelectedPromptItem(null));
    onClose();
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleRunPrompt = async (prompt) => {
    dispatch(setSelectedTileId(null));
    dispatch(setIsActiveChat(true));
    dispatch(setLoading(true));
    dispatch(
      addMessage({
        role: 'user',
        componentType: 'text',
        content: [{ type: 'text', text: prompt }],
      }),
    );

    try {
      const response = await queryDocument(selectedDocument.id, prompt);
      if (response?.result?.choices?.[0]?.message) {
        dispatch(
          addMessage({
            role: 'assistant',
            componentType: 'text',
            content: [
              {
                type: 'text',
                text: response.result.choices[0].message.content,
              },
            ],
          }),
        );
      }
    } catch (err) {
      console.error('Prompt failed:', err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSelectedRow = (doc) => {
    dispatch(setSelectedDocument(doc));
    setShowSelectedDocument(true);
  };

  const handleShowDocumentUpload = () => {
    dispatch(clearSelectedPromptItem());
    setShowDocumentUpload(true);
  };

  // const handleDelete = async () => {
  //   dispatch(setLoading(true));
  //   try {
  //     await deleteDocument(selectedDocument.id, dispatch);
  //     dispatch(
  //       showToast({ message: 'Document deleted!', severity: 'success' }),
  //     );
  //   } catch (err) {
  //     dispatch(
  //       showToast({
  //         message: `Error: ${err.response?.data?.error || err.message}`,
  //         severity: 'error',
  //       }),
  //     );
  //   } finally {
  //     dispatch(setLoading(false));
  //   }
  //   onClose();
  // };

  const handleAddPrompt = () => {
    const promptItem = {
      id: selectedDocument.id,
      type: 'analyze_document',
      document: selectedDocument,
    };
    dispatch(addPromptItem(promptItem));
    dispatch(setSelectedPromptItem(promptItem));
  };

  const handleRemovePrompt = () => {
    dispatch(removePromptItem(selectedItem.id));
    dispatch(clearSelectedPromptItem());
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 450,
        animation: 'fadeIn 0.2s ease-in',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(to bottom, #1e1e1e, #2e2e2e)'
            : 'linear-gradient(180deg, #fff, #e4f2fb, #f2f2f2)',
        boxShadow: 3,
        borderRadius: 2,
        animation: 'fadeIn 0.4s ease-in-out',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <CardHeader
        sx={{ pb: 0 }}
        action={
          <Tooltip title="Cancel">
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        }
        title={
          <SelectedTileHeader
            icon={icon}
            title={title}
            setShowSelectedItem={setShowSelectedDocument}
            selectedItem={
              selectedItem?.document?.fileName ||
              selectedDocument?.fileName ||
              null
            }
            setSelectedItem={(doc) => dispatch(setSelectedDocument(doc))}
          />
        }
      />

      <CardContent>
        {showDocumentUpload ? (
          <DocumentUpload
            setShowDocumentUpload={setShowDocumentUpload}
            setSelectedDocument={setSelectedDocument}
          />
        ) : selectedItem && selectedItem.type === 'analyze_document' ? (
          <DocumentDetail selectedDocument={selectedItem.document} />
        ) : selectedDocument ? (
          <DocumentDetail selectedDocument={selectedDocument} />
        ) : (
          <DocumentTable
            userDocuments={documents.userDocuments}
            handleSelectedRow={handleSelectedRow}
            handleShowDocumentUpload={handleShowDocumentUpload}
          />
        )}
      </CardContent>

      {selectedDocument && (
        <CardActions sx={{ px: 2, mt: -1, mb: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button
              variant="outlined"
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleMenuOpen}
            >
              Quick Prompts
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => handleRunPrompt('Summarize this document.')}
              >
                Summarize this document
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleRunPrompt('Suggest a title for this document.')
                }
              >
                Suggest a title
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleRunPrompt('List key points of this document.')
                }
              >
                List key points
              </MenuItem>
            </Menu>
          </Box>

          <Box>
            {selectedItem ? (
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemovePrompt}
                startIcon={<RemoveCircle />}
              >
                Remove
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleAddPrompt}
                startIcon={<AddCircle />}
              >
                Add
              </Button>
            )}
          </Box>
        </CardActions>
      )}
    </Box>
  );
};

export default DocumentManager;
